import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle, XCircle, AlertCircle, DollarSign } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import type { FairSubmissionStatusResponse } from '../types';

/**
 * Fair Submission Payment Component
 * Issue #966 - Phase 1: Fair Submission Payment Flow
 *
 * Allows Fair Managers to pay submission fees before publishing their fair.
 * Integrates with Stripe checkout for payment processing.
 */
export default function FairSubmissionPayment() {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<FairSubmissionStatusResponse | null>(null);
  const [paymentResult, setPaymentResult] = useState<'success' | 'canceled' | null>(null);

  useEffect(() => {
    // Check for payment result from Stripe redirect
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      setPaymentResult('success');
    } else if (payment === 'canceled') {
      setPaymentResult('canceled');
    }

    fetchSubmissionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairId]);

  const fetchSubmissionStatus = async () => {
    if (!fairId) {
      setError('Fair ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fairManagerApi.getFairSubmissionStatus(parseInt(fairId));
      setStatus(response);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaySubmissionFee = async () => {
    if (!fairId) return;

    try {
      setSubmitting(true);
      setError(null);
      const response = await fairManagerApi.createFairSubmissionCheckout(parseInt(fairId));

      // Redirect to Stripe checkout
      window.location.href = response.checkout_url;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div data-testid="submission-payment-loading" className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div data-testid="submission-payment-error" className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-red-800">Failed to load submission status</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = () => {
    switch (status.submission_payment_status) {
      case 'paid':
        return (
          <span data-testid="payment-status-badge-paid" className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-4 w-4 mr-1" />
            Paid
          </span>
        );
      case 'waived':
        return (
          <span data-testid="payment-status-badge-waived" className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-moonbone text-ink">
            <CheckCircle className="h-4 w-4 mr-1" />
            Waived
          </span>
        );
      case 'failed':
        return (
          <span data-testid="payment-status-badge-failed" className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="h-4 w-4 mr-1" />
            Failed
          </span>
        );
      case 'pending':
      default:
        return (
          <span data-testid="payment-status-badge-pending" className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="h-4 w-4 mr-1" />
            Pending
          </span>
        );
    }
  };

  const canPayFee = status.submission_payment_status === 'pending' && !status.submission_fee_paid;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/fairmanager/fairs')}
          className="text-secondary hover:text-ink flex items-center"
        >
          <span className="mr-1">←</span> Back to Fairs
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Fair Submission Payment</h1>
          {getStatusBadge()}
        </div>

        {/* Payment Result Messages */}
        {paymentResult === 'success' && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <div>
                <p className="text-green-800 font-medium">Payment Successful!</p>
                <p className="text-green-700 text-sm mt-1">
                  Your submission fee has been processed. You can now publish your fair.
                </p>
              </div>
            </div>
          </div>
        )}

        {paymentResult === 'canceled' && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <div>
                <p className="text-yellow-800 font-medium">Payment Canceled</p>
                <p className="text-yellow-700 text-sm mt-1">
                  You canceled the payment process. You can try again below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <XCircle className="h-5 w-5 text-red-600 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Submission Status Information */}
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Payment Status</p>
              <p data-testid="payment-status-text" className="text-lg font-semibold text-gray-900 capitalize">
                {status.submission_payment_status}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Fair Status</p>
              <p data-testid="fair-status-text" className="text-lg font-semibold text-gray-900">
                {status.is_draft ? 'Draft' : 'Published'}
              </p>
            </div>
          </div>

          {status.published_at && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Published Date</p>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(status.published_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}
        </div>

        {/* Payment Information */}
        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">About Submission Fees</h2>
          <div className="bg-moonbone border border-teal-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <DollarSign className="h-5 w-5 text-secondary mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-ink text-sm">
                  Submission fees help us maintain the platform and provide quality service to Fair Managers and vendors.
                  The fee is determined based on your account type and fair details.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {canPayFee ? (
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handlePaySubmissionFee}
                disabled={submitting}
                className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                data-testid="pay-submission-fee-button"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    Pay Submission Fee
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/fairmanager/fairs')}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-600 mb-4">
                {status.submission_payment_status === 'paid' || status.submission_payment_status === 'waived'
                  ? 'Your submission fee has been processed. You can now manage your fair.'
                  : 'Payment is currently unavailable for this fair.'}
              </p>
              <button
                onClick={() => navigate('/fairmanager/fairs')}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition-colors"
              >
                Return to Fairs
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
